import type { ButtonComponentProps } from '@timelyapp/react';
import classNames from 'classnames';
import AdaptiveText from 'components/account-features/AdaptiveText';
import AdaptiveUpgradeButton from 'components/account-features/AdaptiveUpgradeButton';
import {
  getFeaturePlanAvailability,
  getSubscriptionMessage,
  PlanAvailabilityMessage,
} from 'components/account-features/SubscriptionMessages';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  contactAccountManagerWithUpgradeRequest,
  contactWorkspaceAdminWithUpgradeRequest,
} from 'redux/modules/subscriptions.actions';
import type {
  InlineMessageKeyTypes,
  ModalMessageKeyTypes,
} from 'redux/modules/subscriptions.types';
import { getSubscriptionFeaturesByPlan } from 'redux/selectors/accounts';
import { getUpgradePermissionsInfo } from 'redux/selectors/subscriptions';
import type { ThunkDispatch } from 'types/redux';
import styles from './UpgradeState.module.scss';

interface Props {
  className?: string;
  messageKey: InlineMessageKeyTypes | ModalMessageKeyTypes;
  inline?: boolean;
  buttonProps?: ButtonComponentProps;
}

const UpgradeState: React.FC<Props> = ({ buttonProps, className, inline, messageKey }) => {
  const message = getSubscriptionMessage(messageKey);
  const upgradePermissions = useSelector(getUpgradePermissionsInfo);
  const featuresByPlan = useSelector(getSubscriptionFeaturesByPlan);
  const planAvailability = featuresByPlan
    ? getFeaturePlanAvailability(message, featuresByPlan)
    : null;

  const dispatch = useDispatch<ThunkDispatch>();

  const handleContactSupport = useCallback(() => {
    dispatch(contactAccountManagerWithUpgradeRequest(messageKey));
  }, [dispatch, messageKey]);

  const handleContactWorkspaceOwner = useCallback(() => {
    dispatch(contactWorkspaceAdminWithUpgradeRequest(messageKey));
  }, [dispatch, messageKey]);

  return (
    <div className={classNames(styles.content, className, { [styles.inline]: inline })}>
      {message.title && (
        <h5>
          <AdaptiveText text={message.title} />
        </h5>
      )}
      {message.description && (
        <p>
          <AdaptiveText text={message.description} />
        </p>
      )}
      {planAvailability && planAvailability.plansWithFeature.length ? (
        <PlanAvailabilityMessage
          plans={planAvailability.readableSentence}
          numberOfPlans={planAvailability.plansWithFeature.length}
        />
      ) : null}
      <div className={styles.buttons}>
        <AdaptiveUpgradeButton
          hideGotIt
          upgradePermissionsInfo={upgradePermissions}
          featureTrackingKey={message.trackingKey}
          onContactSupport={handleContactSupport}
          onContactWorkspaceOwner={handleContactWorkspaceOwner}
          {...buttonProps}
        />
      </div>
    </div>
  );
};

export default UpgradeState;
